import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  AccountBalanceWalletOutlined,
  AddRounded,
  ArrowForwardRounded,
  CalendarMonthOutlined,
  CircleRounded,
  DirectionsCarOutlined,
  HistoryRounded,
  MoreHorizRounded,
  PeopleAltOutlined,
  PaymentsOutlined,
  RouteOutlined,
  StarRounded,
  WarningAmberOutlined,
  CancelOutlined,
  AccessTimeRounded,
  TaskAltRounded,
} from "@mui/icons-material";
import dashboardApi from "../../Services/Dashboard";
import { apiUrl } from "../../Constants";
import "./HomePage.css";

const CURRENCY = "XAF";

const FALLBACK_TRIPS = [
  {
    id: "BK-24001",
    driverName: "Jean M.",
    riderName: "Marie D.",
    origin: "Douala",
    destination: "Yaoundé",
    requestedAt: "2026-06-21T07:30:00.000Z",
    status: "En cours",
    statusTone: "success",
    fare: 185000,
  },
  {
    id: "BK-24002",
    driverName: "Paul N.",
    riderName: "Entreprise ABC",
    origin: "Bafoussam",
    destination: "Douala",
    requestedAt: "2026-06-21T08:15:00.000Z",
    status: "En attente",
    statusTone: "warning",
    fare: 212000,
  },
  {
    id: "BK-24003",
    driverName: "Eric K.",
    riderName: "Paul T.",
    origin: "Yaoundé",
    destination: "Kribi",
    requestedAt: "2026-06-21T09:00:00.000Z",
    status: "Confirmé",
    statusTone: "success",
    fare: 238000,
  },
  {
    id: "BK-24004",
    driverName: "Nora A.",
    riderName: "Lina B.",
    origin: "Douala",
    destination: "Bamenda",
    requestedAt: "2026-06-21T10:15:00.000Z",
    status: "Annulé",
    statusTone: "danger",
    fare: 154000,
  },
  {
    id: "BK-24005",
    driverName: "Ibrahim S.",
    riderName: "Koffi S.",
    origin: "Buea",
    destination: "Yaoundé",
    requestedAt: "2026-06-21T11:10:00.000Z",
    status: "En cours",
    statusTone: "success",
    fare: 265000,
  },
];

const FALLBACK_DRIVERS = [
  {
    id: "DRV-1",
    firstName: "Jean",
    lastName: "M.",
    rating: 4.9,
    courses: 124,
    status: "APPROVED",
    avatar: "",
  },
  {
    id: "DRV-2",
    firstName: "Paul",
    lastName: "N.",
    rating: 4.8,
    courses: 98,
    status: "APPROVED",
    avatar: "",
  },
  {
    id: "DRV-3",
    firstName: "Eric",
    lastName: "K.",
    rating: 4.7,
    courses: 85,
    status: "APPROVED",
    avatar: "",
  },
];

const FALLBACK_ALERTS = [
  {
    tone: "warning",
    icon: WarningAmberOutlined,
    title: "Assurance véhicule expire dans 7 jours",
    detail: "Toyota Hiace - immatriculation 2741-AB",
  },
  {
    tone: "danger",
    icon: CancelOutlined,
    title: "Permis conducteur à renouveler",
    detail: "Paul N. doit mettre à jour son document",
  },
  {
    tone: "success",
    icon: TaskAltRounded,
    title: "Tous les documents validés",
    detail: "Aucune action requise pour le moment",
  },
];

const FALLBACK_VALIDATIONS = [
  { label: "Nouveaux conducteurs", value: 3, tone: "success" },
  { label: "Nouveaux véhicules", value: 2, tone: "info" },
  { label: "Documents à vérifier", value: 5, tone: "warning" },
];

const FALLBACK_SERIES = {
  today: [180, 220, 200, 300, 260, 380, 340, 420],
  week: [120, 180, 210, 240, 320, 360, 480],
  month: [180, 220, 210, 300, 360, 340, 450, 560, 620, 700, 760, 900],
};

const FALLBACK_DASHBOARD = {
  summary: {
    companyName: "Lamar Enterprise",
    companyStatus: "ACTIVE",
    totalDrivers: 48,
    totalVehicles: 22,
    tripsToday: 14,
    currentDate: new Date().toISOString().slice(0, 10),
  },
  kpi: {
    activeDrivers: 31,
    driverEvolution: {
      currentMonth: 12,
      previousMonth: 9,
      percentChange: 33.3,
    },
    vehiclesAvailable: 11,
    vehiclesInMaintenance: 3,
    tripsToday: 14,
    tripsInProgress: 5,
    revenueMonth: 245000.5,
    revenueGrowthPercent: 18.7,
  },
  ongoingTrips: [
    {
      id: "9f0f6b27-4df7-4cf6-9f4f-7d5d91a8d001",
      driver: {
        id: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d002",
        name: "Jean Kouassi",
        photo: "",
      },
      departure: "Akwa, Douala",
      destination: "Bonapriso, Douala",
      departureTime: "2026-06-21T08:10:00.000Z",
      arrivalTime: "2026-06-21T08:45:00.000Z",
      status: "STARTED",
    },
    {
      id: "9f0f6b27-4df7-4cf6-9f4f-7d5d91a8d002",
      driver: {
        id: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d003",
        name: "Awa T.",
        photo: "",
      },
      departure: "Bastos, Yaounde",
      destination: "Mvan, Yaounde",
      departureTime: "2026-06-21T09:20:00.000Z",
      arrivalTime: "2026-06-21T09:55:00.000Z",
      status: "CONFIRMED",
    },
    {
      id: "9f0f6b27-4df7-4cf6-9f4f-7d5d91a8d003",
      driver: {
        id: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d004",
        name: "Paul N.",
        photo: "",
      },
      departure: "Bafoussam",
      destination: "Douala",
      departureTime: "2026-06-21T10:05:00.000Z",
      arrivalTime: "2026-06-21T12:15:00.000Z",
      status: "PENDING",
    },
  ],
  recentReservations: {
    newReservations: 8,
    pendingRequests: 3,
    cancellationsToday: 1,
    items: [
      {
        id: "b3f2c2fb-7a1a-4a7a-8b7a-9c5c2d1d1111",
        client: {
          id: "c4f2c2fb-7a1a-4a7a-8b7a-9c5c2d1d2222",
          name: "Amina N",
          photo: "",
        },
        trip: "Bonanjo -> Akwa",
        date: "2026-06-21T09:02:00.000Z",
        status: "confirmed",
      },
      {
        id: "b3f2c2fb-7a1a-4a7a-8b7a-9c5c2d1d3333",
        client: {
          id: "c4f2c2fb-7a1a-4a7a-8b7a-9c5c2d1d4444",
          name: "Entreprise ABC",
          photo: "",
        },
        trip: "Douala -> Bafoussam",
        date: "2026-06-21T08:45:00.000Z",
        status: "pending",
      },
      {
        id: "b3f2c2fb-7a1a-4a7a-8b7a-9c5c2d1d5555",
        client: {
          id: "c4f2c2fb-7a1a-4a7a-8b7a-9c5c2d1d6666",
          name: "Paul T.",
          photo: "",
        },
        trip: "Kribi -> Douala",
        date: "2026-06-21T07:25:00.000Z",
        status: "cancelled",
      },
    ],
  },
  fleetOccupancy: {
    totalVehicles: 22,
    available: 11,
    inCirculation: 8,
    maintenance: 3,
    percentages: {
      available: 50,
      inCirculation: 36.4,
      maintenance: 13.6,
    },
  },
  revenues: {
    byDay: [
      { date: "2026-06-01", amount: 12000 },
      { date: "2026-06-02", amount: 15800 },
      { date: "2026-06-03", amount: 9900 },
      { date: "2026-06-04", amount: 16400 },
      { date: "2026-06-05", amount: 22100 },
      { date: "2026-06-06", amount: 18000 },
      { date: "2026-06-07", amount: 24500 },
    ],
    totalMonth: 245000.5,
    totalWeek: 61250,
    totalToday: 18400,
  },
  topDrivers: [
    {
      id: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d002",
      name: "Jean Kouassi",
      averageRating: 4.8,
      tripsCount: 42,
      photo: "",
      revenueGenerated: 84500,
    },
    {
      id: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d003",
      name: "Awa T.",
      averageRating: 4.7,
      tripsCount: 38,
      photo: "",
      revenueGenerated: 73200,
    },
    {
      id: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d004",
      name: "Paul N.",
      averageRating: 4.6,
      tripsCount: 33,
      photo: "",
      revenueGenerated: 64100,
    },
  ],
  alerts: {
    vehicleInsuranceExpiringSoon: {
      count: 2,
      items: [
        {
          vehicleId: "veh-001",
          vehicleNumber: "LT-1234-AB",
          vehicleName: "Toyota Hiace",
          driver: "Jean Kouassi",
          driverPhoto: "",
          expiresAt: "2026-07-10T00:00:00.000Z",
        },
      ],
    },
    driverLicenseExpiringSoon: {
      count: 1,
      items: [
        {
          driverId: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d002",
          driver: "Jean Kouassi",
          photo: "",
          expiresAt: "2026-06-30T00:00:00.000Z",
        },
      ],
    },
    documentsMissingOrExpired: {
      count: 4,
      items: [
        {
          driverId: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d002",
          driver: "Jean Kouassi",
          photo: "",
          missingDocuments: ["Proof of address"],
          expiredDocuments: [],
        },
      ],
    },
    documentsValidated: {
      count: 18,
      items: [
        {
          documentId: "doc-001",
          driverId: "2b0f6b27-4df7-4cf6-9f4f-7d5d91a8d002",
          driver: "Jean Kouassi",
          photo: "",
          documentType: "Driver license",
          validatedAt: "2026-06-20T15:12:00.000Z",
        },
      ],
    },
  },
  validationRequests: {
    newDrivers: {
      count: 2,
      items: [
        {
          driverId: "drv-001",
          driver: "Moussa Ali",
          photo: "",
          status: "ONBOARDING",
          submittedAt: "2026-06-21T07:18:00.000Z",
        },
      ],
    },
    newVehicles: {
      count: 1,
      items: [
        {
          vehicleId: "veh-009",
          vehicleNumber: "LT-7788-CD",
          vehicleName: "Nissan Urvan",
          driver: "Awa T.",
          photo: "",
          status: "INACTIVE",
          submittedAt: "2026-06-20T10:00:00.000Z",
        },
      ],
    },
    documentsToVerify: {
      count: 3,
      items: [
        {
          documentId: "doc-004",
          driverId: "drv-001",
          driver: "Moussa Ali",
          photo: "",
          documentType: "Proof of address",
          status: "ACCESSING",
          submittedAt: "2026-06-21T07:18:00.000Z",
        },
      ],
    },
  },
  wallet: {
    availableBalance: 120500.75,
    currency: CURRENCY,
    totalReceived: 250000,
    totalWithdrawn: 129499.25,
    recentTransactions: [
      {
        id: "pay-001",
        reference: "LP-20260621-0001",
        amount: 18400,
        currency: CURRENCY,
        direction: "receive",
        type: "payment",
        status: "succeeded",
        description: "Ride settlement",
        counterpartyName: "John Doe",
        merchantName: "Metuaa",
        createdAt: "2026-06-21T09:10:00.000Z",
      },
    ],
  },
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function resolveImageUrl(value) {
  const text = firstText(value);
  if (!text) {
    return "";
  }

  if (/^https?:\/\//i.test(text)) {
    return text;
  }

  return `${apiUrl}${text}`;
}

function formatPercent(value, digits = 1, showSign = true) {
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "0%";
  }

  const suffix = showSign && num > 0 ? "+" : "";
  return `${suffix}${num.toFixed(digits).replace(/\.0+$/, "")}%`;
}

function formatDateTimeShort(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(/\./g, "");
}

function getCompanyStatusMeta(status, formatMessage) {
  const normalized = normalizeText(status);

  if (normalized === "active" || normalized === "enabled") {
    return { label: formatMessage ? formatMessage({ id: "dashboard.companyStatus.active" }) : "Entreprise active", tone: "success" };
  }

  if (normalized.includes("pend")) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.companyStatus.pending" }) : "Entreprise en attente", tone: "warning" };
  }

  if (normalized.includes("suspend") || normalized.includes("inactive")) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.companyStatus.inactive" }) : "Entreprise inactive", tone: "danger" };
  }

  return { label: firstText(status, "Entreprise"), tone: "neutral" };
}

function scaleSeries(template, total) {
  const baseTotal = template.reduce((sum, value) => sum + value, 0);
  const numericTotal = Number(total);

  if (!Number.isFinite(numericTotal) || numericTotal <= 0 || baseTotal <= 0) {
    return template;
  }

  const factor = numericTotal / baseTotal;
  return template.map((value) => Math.max(0, Math.round(value * factor)));
}

function splitRouteText(value, formatMessage) {
  const text = firstText(value);

  if (!text) {
    return [formatMessage ? formatMessage({ id: "dashboard.route.departure" }) : "Départ", formatMessage ? formatMessage({ id: "dashboard.route.arrival" }) : "Arrivée"];
  }

  const separators = [" -> ", "→", " - "];
  for (const separator of separators) {
    if (text.includes(separator)) {
      const [left, ...rest] = text.split(separator);
      return [left.trim() || (formatMessage ? formatMessage({ id: "dashboard.route.departure" }) : "Départ"), rest.join(separator).trim() || (formatMessage ? formatMessage({ id: "dashboard.route.arrival" }) : "Arrivée")];
    }
  }

  return [text, formatMessage ? formatMessage({ id: "dashboard.route.arrival" }) : "Arrivée"];
}

function normalizeOngoingTrip(item, index, formatMessage) {
  const driverName = firstText(item?.driver?.name, item?.driverName, item?.driver, `Conducteur ${index + 1}`);
  const departureTime = firstText(item?.departureTime, item?.startTime, item?.createdAt);
  const arrivalTime = firstText(item?.arrivalTime, item?.endTime);
  const statusMeta = getStatusMeta(item?.status, formatMessage);

  return {
    id: firstText(item?.id, item?._id, `${index + 1}`),
    bookingId: firstText(item?.id, item?._id, `TR-${index + 1}`),
    driverName,
    avatar: firstText(item?.driver?.photo, item?.driver?.image, item?.driverImage, item?.driverImage_local),
    origin: firstText(item?.departure, item?.origin, formatMessage ? formatMessage({ id: "dashboard.route.departure" }) : "Départ"),
    destination: firstText(item?.destination, item?.dropoff, formatMessage ? formatMessage({ id: "dashboard.route.arrival" }) : "Arrivée"),
    requestedDate: departureTime,
    requestedLabel: departureTime ? formatTime(departureTime) : "--:--",
    destinationTimeLabel: arrivalTime ? formatTime(arrivalTime) : "--:--",
    statusValue: item?.status,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    raw: item,
  };
}

function normalizeReservationItem(item, index, formatMessage) {
  const [origin, destination] = splitRouteText(item?.trip, formatMessage);
  const statusMeta = getStatusMeta(item?.status, formatMessage);
  const requestedDate = firstText(item?.date, item?.createdAt);
  const clientName = firstText(item?.client?.name, item?.clientName, item?.name, `Réservation ${index + 1}`);

  return {
    id: firstText(item?.id, item?._id, `${index + 1}`),
    bookingId: firstText(item?.id, item?._id, `BK-${index + 1}`),
    riderName: clientName,
    origin,
    destination,
    requestedAt: requestedDate,
    requestedDate: requestedDate,
    requestedDisplay: requestedDate ? formatDateTimeShort(requestedDate) : "--",
    statusValue: item?.status,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    raw: item,
  };
}

function normalizeTopDriverItem(item, index) {
  const name = firstText(item?.name, `Conducteur ${index + 1}`);
  const rating = toNumber(item?.averageRating, 0);
  const courses = toNumber(item?.tripsCount, 0);
  const revenueGenerated = toNumber(item?.revenueGenerated, 0);

  return {
    id: firstText(item?.id, item?._id, `${index + 1}`),
    name,
    rating,
    courses,
    statusLabel: revenueGenerated ? formatCurrency(revenueGenerated, item?.currency || CURRENCY) : `${formatNumber(courses)} courses`,
    avatar: firstText(item?.photo, item?.avatar, item?.image_local, item?.image),
    raw: item,
  };
}

function buildFleetStatsFromDashboard(dashboard) {
  const summary = safeObject(dashboard?.summary);
  const kpi = safeObject(dashboard?.kpi);
  const fleet = safeObject(dashboard?.fleetOccupancy);
  const availableValue = fleet.available ?? kpi.vehiclesAvailable;
  const maintenanceValue = fleet.maintenance ?? kpi.vehiclesInMaintenance;
  const totalValue = fleet.totalVehicles ?? summary.totalVehicles;
  const derivedInCirculation = Math.max(
    toNumber(totalValue, 0) - toNumber(availableValue, 0) - toNumber(maintenanceValue, 0),
    0
  );
  const inCirculationValue = fleet.inCirculation;
  const totalFallback = toNumber(availableValue, 0) + toNumber(maintenanceValue, 0) + toNumber(inCirculationValue, derivedInCirculation);

  return {
    total: toNumber(totalValue, totalFallback),
    available: toNumber(availableValue, FALLBACK_DASHBOARD.fleetOccupancy.available),
    inCirculation: toNumber(inCirculationValue, derivedInCirculation),
    maintenance: toNumber(maintenanceValue, FALLBACK_DASHBOARD.fleetOccupancy.maintenance),
    percentages: safeObject(fleet.percentages),
  };
}

function buildAlertItemsFromDashboard(alerts, formatMessage) {
  const items = [];
  const insurance = safeObject(alerts?.vehicleInsuranceExpiringSoon);
  const license = safeObject(alerts?.driverLicenseExpiringSoon);
  const missingDocs = safeObject(alerts?.documentsMissingOrExpired);
  const validatedDocs = safeObject(alerts?.documentsValidated);

  if (toNumber(insurance.count, 0) > 0) {
    const first = safeArray(insurance.items)[0] || {};
    items.push({
      tone: "warning",
      icon: WarningAmberOutlined,
      title: formatMessage ? formatMessage({ id: "dashboard.alert.insuranceExpiring" }, { count: formatNumber(insurance.count) }) : `Assurance véhicule expire dans ${formatNumber(insurance.count)} jour${toNumber(insurance.count, 0) > 1 ? "s" : ""}`,
      detail: first.vehicleName
        ? `${first.vehicleName}${first.vehicleNumber ? ` • ${first.vehicleNumber}` : ""}${first.driver ? ` • ${first.driver}` : ""}`
        : (formatMessage ? formatMessage({ id: "dashboard.alert.vehiclesAtRisk" }) : "Vérifiez les véhicules à risque."),
    });
  }

  if (toNumber(license.count, 0) > 0) {
    const first = safeArray(license.items)[0] || {};
    items.push({
      tone: "danger",
      icon: CancelOutlined,
      title: formatMessage ? formatMessage({ id: "dashboard.alert.licenseExpiring" }) : `Permis conducteur à renouveler`,
      detail: first.driver
        ? `${first.driver}${first.expiresAt ? ` • ${formatDateTimeShort(first.expiresAt)}` : ""}`
        : (formatMessage ? formatMessage({ id: "dashboard.alert.licenseExpiringDetail" }, { count: formatNumber(license.count) }) : `${formatNumber(license.count)} conducteur${toNumber(license.count, 0) > 1 ? "s" : ""} concernés`),
    });
  }

  if (toNumber(missingDocs.count, 0) > 0) {
    const first = safeArray(missingDocs.items)[0] || {};
    const missing = safeArray(first.missingDocuments).join(", ");
    const expired = safeArray(first.expiredDocuments).join(", ");
    items.push({
      tone: "warning",
      icon: AccessTimeRounded,
      title: formatMessage ? formatMessage({ id: "dashboard.alert.docsToCheck" }, { count: formatNumber(missingDocs.count) }) : `${formatNumber(missingDocs.count)} document${toNumber(missingDocs.count, 0) > 1 ? "s" : ""} à vérifier`,
      detail: first.driver
        ? `${first.driver}${missing ? ` • ${missing}` : ""}${expired ? ` • ${expired}` : ""}`
        : (formatMessage ? formatMessage({ id: "dashboard.alert.docsToCheckDetail" }) : "Des dossiers sont incomplets ou expirés."),
    });
  }

  if (!items.length && toNumber(validatedDocs.count, 0) > 0) {
    items.push({
      tone: "success",
      icon: TaskAltRounded,
      title: formatMessage ? formatMessage({ id: "dashboard.alert.allDocsValidated" }) : "Tous les documents validés",
      detail: formatMessage ? formatMessage({ id: "dashboard.alert.allDocsValidatedDetail" }, { count: formatNumber(validatedDocs.count) }) : `${formatNumber(validatedDocs.count)} document${toNumber(validatedDocs.count, 0) > 1 ? "s" : ""} validé${toNumber(validatedDocs.count, 0) > 1 ? "s" : ""} récemment`,
    });
  }

  if (!items.length) {
    items.push({
      tone: "success",
      icon: TaskAltRounded,
      title: formatMessage ? formatMessage({ id: "dashboard.alert.allDocsValidated" }) : "Tous les documents validés",
      detail: formatMessage ? formatMessage({ id: "dashboard.alert.noAction" }) : "Aucune action requise pour le moment.",
    });
  }

  return items.slice(0, 3);
}

function buildValidationRowsFromDashboard(validationRequests, formatMessage) {
  const requestData = safeObject(validationRequests);

  return [
    {
      label: formatMessage ? formatMessage({ id: "dashboard.validation.newDrivers" }) : "Nouveaux conducteurs",
      value: toNumber(requestData?.newDrivers?.count, 0),
      tone: "success",
    },
    {
      label: formatMessage ? formatMessage({ id: "dashboard.validation.newVehicles" }) : "Nouveaux véhicules",
      value: toNumber(requestData?.newVehicles?.count, 0),
      tone: "info",
    },
    {
      label: formatMessage ? formatMessage({ id: "dashboard.validation.docsToCheck" }) : "Documents à vérifier",
      value: toNumber(requestData?.documentsToVerify?.count, 0),
      tone: "warning",
    },
  ];
}

function buildRevenueSeriesFromDashboard(revenues, period, selectedDate) {
  const revenueData = safeObject(revenues);
  const byDay = safeArray(revenueData.byDay).map((item) => ({
    date: item?.date,
    amount: toNumber(item?.amount, 0),
  }));
  const referenceDate = isValidDate(selectedDate) ? new Date(selectedDate) : new Date();

  if (period === "today") {
    const totalToday = toNumber(revenueData.totalToday, 0);
    const lastEntry = byDay.length ? byDay[byDay.length - 1] : null;
    const total = totalToday > 0 ? totalToday : lastEntry?.amount || 0;
    return {
      values: total > 0 ? scaleSeries(FALLBACK_SERIES.today, total) : FALLBACK_SERIES.today,
      labels: ["00h", "03h", "06h", "09h", "12h", "15h", "18h", "21h"],
    };
  }

  if (period === "week") {
    const start = addDays(referenceDate, -6);
    const days = Array.from({ length: 7 }, (_, index) => addDays(start, index));
    const values = days.map((day) =>
      byDay.reduce((sum, item) => {
        if (item.date && sameLocalDay(item.date, day)) {
          return sum + item.amount;
        }

        return sum;
      }, 0)
    );

    if (values.some((value) => value > 0)) {
      return {
        values,
        labels: days.map((day) =>
          new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(day).replace(/\./g, "")
        ),
      };
    }

    const totalWeek = toNumber(revenueData.totalWeek, 0);
    return {
      values: totalWeek > 0 ? scaleSeries(FALLBACK_SERIES.week, totalWeek) : FALLBACK_SERIES.week,
      labels: days.map((day) =>
        new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(day).replace(/\./g, "")
      ),
    };
  }

  if (byDay.length > 0) {
    return {
      values: byDay.map((item) => item.amount),
      labels: byDay.map((item) =>
        formatShortDate(item.date || referenceDate)
      ),
    };
  }

  const totalMonth = toNumber(revenueData.totalMonth, 0);
  return {
    values: totalMonth > 0 ? scaleSeries(FALLBACK_SERIES.month, totalMonth) : FALLBACK_SERIES.month,
    labels: ["S1", "S2", "S3", "S4", "S5", "S6", "S7", "S8", "S9", "S10", "S11", "S12"],
  };
}

function readMaybeJson(value) {
  if (!value) {
    return {};
  }

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
}

function firstText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const text = String(value).trim();
    if (text) {
      return text;
    }
  }

  return "";
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function formatNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return "0";
  }

  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 })
    .format(Math.round(num))
    .replace(/\u202f/g, " ");
}

function formatCurrency(value, suffix = CURRENCY) {
  return `${formatNumber(value)} ${suffix}`;
}

function formatDateInput(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatLongDate(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })
    .format(date)
    .replace(/\b([A-ZÀ-Ÿ])/g, (match) => match.toLowerCase());
}

function formatShortDate(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
  })
    .format(date)
    .replace(/\./g, "");
}

function formatTime(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--:--";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isValidDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(date.getTime());
}

function sameLocalDay(a, b) {
  const left = a instanceof Date ? a : new Date(a);
  const right = b instanceof Date ? b : new Date(b);

  if (!isValidDate(left) || !isValidDate(right)) {
    return false;
  }

  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function sameLocalMonth(a, b) {
  const left = a instanceof Date ? a : new Date(a);
  const right = b instanceof Date ? b : new Date(b);

  if (!isValidDate(left) || !isValidDate(right)) {
    return false;
  }

  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth()
  );
}

function addDays(date, amount) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + amount);
  return copy;
}

function getStatusMeta(status, formatMessage) {
  const normalized = normalizeText(status);

  if (normalized.includes("annul") || normalized.includes("cancel")) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.status.cancelled" }) : "Annulé", tone: "danger" };
  }

  if (normalized.includes("en attente") || normalized.includes("pending")) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.status.pending" }) : "En attente", tone: "warning" };
  }

  if (
    normalized.includes("confirm") ||
    normalized.includes("approve") ||
    normalized.includes("accept") ||
    normalized.includes("completed")
  ) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.status.confirmed" }) : "Confirmé", tone: "success" };
  }

  if (normalized.includes("cours") || normalized.includes("progress") || normalized.includes("start")) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.status.inProgress" }) : "En cours", tone: "success" };
  }

  if (normalized.includes("scheduled") || normalized.includes("planned")) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.status.scheduled" }) : "Programmé", tone: "info" };
  }

  return { label: firstText(status, formatMessage ? formatMessage({ id: "dashboard.status.trip" }) : "Trajet"), tone: "neutral" };
}

function getDriverStatusMeta(status, formatMessage) {
  const normalized = normalizeText(status);

  if (normalized === "approved" || normalized === "active") {
    return { label: formatMessage ? formatMessage({ id: "dashboard.driverStatus.active" }) : "Actif", tone: "success" };
  }

  if (normalized === "banned" || normalized === "rejected") {
    return { label: formatMessage ? formatMessage({ id: "dashboard.driverStatus.blocked" }) : "Bloqué", tone: "danger" };
  }

  return { label: formatMessage ? formatMessage({ id: "dashboard.status.pending" }) : "En attente", tone: "warning" };
}

function getVehicleStatusMeta(status, formatMessage) {
  const normalized = normalizeText(status);

  if (normalized === "1" || normalized === "active" || normalized === "approved") {
    return { label: formatMessage ? formatMessage({ id: "dashboard.vehicleStatus.available" }) : "Disponible", tone: "success" };
  }

  if (normalized.includes("maint")) {
    return { label: formatMessage ? formatMessage({ id: "dashboard.vehicleStatus.maintenance" }) : "Maintenance", tone: "warning" };
  }

  return { label: formatMessage ? formatMessage({ id: "dashboard.vehicleStatus.unavailable" }) : "Indisponible", tone: "neutral" };
}

function buildInitials(value) {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return "ME";
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function extractListFromResponse(response) {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  const candidates = [
    payload?.data?.triplists,
    payload?.data?.data?.triplists,
    payload?.data?.data,
    payload?.data?.results,
    payload?.data?.rides,
    payload?.data?.bookings,
    payload?.data,
    payload?.triplists,
    payload?.results,
    payload?.rides,
    payload?.bookings,
    payload,
  ];

  return candidates.find((value) => Array.isArray(value)) || [];
}

function extractObjectFromResponse(response) {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  return Array.isArray(payload) ? {} : payload;
}

function normalizeTripItem(item, index, formatMessage) {
  const requestedAt = firstText(
    item?.requestedAt,
    item?.createdAt,
    item?.created_at,
    item?.updatedAt,
    item?.tripDate
  );

  const requestedDate = isValidDate(requestedAt) ? new Date(requestedAt) : null;
  const pickup = firstText(
    item?.pickupAddress,
    item?.pickup_address,
    item?.originLabel,
    item?.origin,
    item?.from,
    item?.pickupPlace?.name,
    item?.pickupPlace,
    item?.userRideRequestData?.pickupAddress
  );
  const drop = firstText(
    item?.dropAddress,
    item?.drop_address,
    item?.destinationLabel,
    item?.destination,
    item?.to,
    item?.dropPlace?.name,
    item?.dropPlace,
    item?.userRideRequestData?.dropAddress
  );

  const riderName = firstText(
    item?.userRideRequestData?.user_name,
    item?.user_name,
    item?.customer_name,
    item?.passengerName,
    item?.bookingUserName,
    item?.clientName,
    item?.name,
    item?.bookingId,
    `Réservation ${index + 1}`
  );

  const driverName = firstText(
    item?.driverName?.firstName && item?.driverName?.lastName
      ? `${item.driverName.firstName} ${item.driverName.lastName}`
      : "",
    item?.driverName,
    item?.drivername,
    item?.driver_details?.user_name,
    item?.driver_details?.name,
    `${item?.driver_details?.first_name || ""} ${item?.driver_details?.last_name || ""}`.trim(),
    item?.assignedDriver?.firstName && item?.assignedDriver?.lastName
      ? `${item.assignedDriver.firstName} ${item.assignedDriver.lastName}`
      : "",
    item?.bookingId
  );

  const statusValue = firstText(item?.tripStatus, item?.status, item?.bookingStatus);
  const statusMeta = getStatusMeta(statusValue, formatMessage);
  const fare = toNumber(
    firstText(
      item?.driverEarnings?.earnings,
      item?.estimatedFare,
      item?.total_amount,
      item?.totalAmount,
      item?.fare,
      item?.amount,
      item?.price
    ),
    0
  );

  return {
    id: firstText(item?._id, item?.id, item?.bookingId, `${index + 1}`),
    bookingId: firstText(item?.bookingId, item?._id, `BK-${index + 1}`),
    riderName,
    driverName,
    origin: pickup || "Départ",
    destination: drop || "Arrivée",
    requestedAt,
    requestedDate,
    requestedLabel: requestedDate ? formatTime(requestedDate) : "--:--",
    requestedDisplay: requestedDate ? (formatMessage ? formatMessage({ id: "dashboard.chart.todayWithTime" }, { time: formatTime(requestedDate) }) : `Aujourd'hui, ${formatTime(requestedDate)}`) : "--",
    statusValue,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    fare,
    raw: item,
  };
}

function normalizeDriverItem(item, index, ratingsMap, formatMessage) {
  const fullName = firstText(
    item?.firstName && item?.lastName ? `${item.firstName} ${item.lastName}` : "",
    item?.user_name,
    item?.driver_ride_details?.user_name,
    item?.name,
    `Conducteur ${index + 1}`
  );
  const statusValue = firstText(
    item?.driver_ride_details?.accountStatus,
    item?.accountStatus,
    item?.status,
    item?.driverStatus
  );
  const statusMeta = getDriverStatusMeta(statusValue, formatMessage);
  const totalRequests = toNumber(
    firstText(
      item?.driver_ride_details?.total_requests?.length,
      item?.driver_ride_details?.totalRequests,
      item?.driver_ride_details?.accepted_requests?.length,
      item?.driver_ride_metrics?.totalRidesCount,
      item?.total_requests,
      item?.ridesaccepted?.length
    ),
    0
  );
  const acceptedRequests = toNumber(
    firstText(
      item?.driver_ride_details?.accepted_requests?.length,
      item?.driver_ride_metrics?.acceptedRidesCount,
      item?.accepted_requests,
      item?.ridesaccepted?.length
    ),
    0
  );

  const rawRatingCandidates = [
    item?._id,
    item?.id,
    item?.driver_ride_details?._id,
    item?.driver_ride_details?.id,
    normalizeText(fullName),
  ];

  let ratings = [];
  for (const key of rawRatingCandidates) {
    const value = ratingsMap.get(key);
    if (value?.length) {
      ratings = value;
      break;
    }
  }

  const rating =
    ratings.length > 0
      ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length
      : totalRequests > 0
        ? Math.min(5, 4.1 + (acceptedRequests / Math.max(totalRequests, 1)) * 0.9)
        : 4.2 - index * 0.1;

  const avatar = firstText(item?.image_local, item?.avatar_url, item?.image, "");

  return {
    id: firstText(item?._id, item?.id, `${index + 1}`),
    name: fullName,
    rating,
    courses: acceptedRequests || totalRequests || 0,
    totalRequests,
    acceptedRequests,
    statusValue,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    avatar,
    initials: buildInitials(fullName),
    raw: item,
  };
}

function normalizeVehicleItem(item, index, formatMessage) {
  const name = firstText(item?.vehicle_name, item?.vehicleBrand, `Véhicule ${index + 1}`);
  const plate = firstText(item?.vehicle_no, item?.plateNumber, item?.licensePlate, "--");
  const driverName = firstText(
    item?.assignedDriver?.firstName && item?.assignedDriver?.lastName
      ? `${item.assignedDriver.firstName} ${item.assignedDriver.lastName}`
      : "",
    item?.assignedDriver?.name,
    item?.assignedDriver?.user_name,
    "--"
  );
  const statusValue = firstText(item?.status, item?.vehicle_status, item?.availabilityStatus);
  const statusMeta = getVehicleStatusMeta(statusValue, formatMessage);
  const isActive = normalizeText(statusValue) === "1" || normalizeText(statusValue) === "active" || normalizeText(statusValue) === "approved";

  return {
    id: firstText(item?._id, item?.id, `${index + 1}`),
    name,
    plate,
    driverName,
    statusValue,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    isActive,
    hasDriver: driverName !== "--",
    insuranceExp: firstText(item?.insurance_exp, item?.insuranceExp, item?.insuranceExpiryDate),
    raw: item,
  };
}

function getVehicleAssignmentStats(vehicles) {
  if (!vehicles.length) {
    return {
      total: 22,
      available: 18,
      inCirculation: 12,
      maintenance: 4,
    };
  }

  const activeVehicles = vehicles.filter((vehicle) => vehicle.isActive);
  const assignedVehicles = activeVehicles.filter((vehicle) => vehicle.hasDriver);
  const maintenanceVehicles = vehicles.filter((vehicle) => !vehicle.isActive);

  return {
    total: vehicles.length,
    available: activeVehicles.length,
    inCirculation: assignedVehicles.length,
    maintenance: maintenanceVehicles.length,
  };
}

function buildRatingsMap(ratings) {
  const map = new Map();

  ratings.forEach((rating) => {
    const ratingValue = toNumber(rating?.rating, null);
    if (ratingValue === null) {
      return;
    }

    const candidates = [
      rating?.driverdetails?._id,
      rating?.driverdetails?.id,
      rating?.driver_id,
      rating?.driverId,
      normalizeText(
        firstText(
          rating?.driverdetails?.user_name,
          `${rating?.driverdetails?.first_name || ""} ${rating?.driverdetails?.last_name || ""}`.trim(),
          rating?.drivername,
          rating?.driver_name
        )
      ),
    ].filter(Boolean);

    candidates.forEach((key) => {
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(ratingValue);
    });
  });

  return map;
}

function buildSeriesFromTrips(trips, period, selectedDate) {
  const referenceDate = isValidDate(selectedDate) ? new Date(selectedDate) : new Date();

  if (!trips.length) {
    return {
      values: FALLBACK_SERIES[period],
      labels:
        period === "today"
          ? ["00h", "03h", "06h", "09h", "12h", "15h", "18h", "21h"]
          : period === "week"
            ? ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]
            : ["S1", "S2", "S3", "S4", "S5", "S6", "S7", "S8", "S9", "S10", "S11", "S12"],
    };
  }

  if (period === "today") {
    const buckets = [
      { start: 0, end: 3, label: "00h" },
      { start: 3, end: 6, label: "03h" },
      { start: 6, end: 9, label: "06h" },
      { start: 9, end: 12, label: "09h" },
      { start: 12, end: 15, label: "12h" },
      { start: 15, end: 18, label: "15h" },
      { start: 18, end: 21, label: "18h" },
      { start: 21, end: 24, label: "21h" },
    ];

    const values = buckets.map((bucket) =>
      trips.reduce((sum, trip) => {
        if (!trip.requestedDate || !sameLocalDay(trip.requestedDate, referenceDate)) {
          return sum;
        }

        const hour = trip.requestedDate.getHours();
        if (hour >= bucket.start && hour < bucket.end) {
          return sum + trip.fare;
        }

        return sum;
      }, 0)
    );

    if (values.every((value) => value === 0)) {
      return {
        values: FALLBACK_SERIES.today,
        labels: buckets.map((bucket) => bucket.label),
      };
    }

    return {
      values,
      labels: buckets.map((bucket) => bucket.label),
    };
  }

  if (period === "week") {
    const start = addDays(referenceDate, -6);
    const days = Array.from({ length: 7 }, (_, index) => addDays(start, index));
    const values = days.map((day) =>
      trips.reduce((sum, trip) => {
        if (trip.requestedDate && sameLocalDay(trip.requestedDate, day)) {
          return sum + trip.fare;
        }
        return sum;
      }, 0)
    );

    if (values.every((value) => value === 0)) {
      return {
        values: FALLBACK_SERIES.week,
        labels: days.map((day) =>
          new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(day).replace(/\./g, "")
        ),
      };
    }

    return {
      values,
      labels: days.map((day) =>
        new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(day).replace(/\./g, "")
      ),
    };
  }

  const monthStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const daysInMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0).getDate();
  const bucketSize = Math.max(1, Math.ceil(daysInMonth / 12));
  const buckets = Array.from({ length: Math.ceil(daysInMonth / bucketSize) }, (_, index) => {
    const startDay = index * bucketSize + 1;
    const endDay = Math.min(daysInMonth, startDay + bucketSize - 1);
    return { startDay, endDay };
  });
  const values = buckets.map((bucket) =>
    trips.reduce((sum, trip) => {
      if (!trip.requestedDate || !sameLocalMonth(trip.requestedDate, referenceDate)) {
        return sum;
      }

      const dayOfMonth = trip.requestedDate.getDate();
      if (dayOfMonth >= bucket.startDay && dayOfMonth <= bucket.endDay) {
        return sum + trip.fare;
      }

      return sum;
    }, 0)
  );

  if (values.every((value) => value === 0)) {
    return {
      values: FALLBACK_SERIES.month,
      labels: buckets.map((bucket) => `${bucket.startDay}-${bucket.endDay}`),
    };
  }

  return {
    values,
    labels: buckets.map((bucket) => `${bucket.startDay}-${bucket.endDay}`),
  };
}

function StatCard({ icon: Icon, title, value, meta, tone = "green", sparkline = FALLBACK_SERIES.week }) {
  return (
    <article className={`de-stat-card de-tone-${tone}`}>
      <div className="de-stat-card-head">
        <span className="de-stat-icon" aria-hidden="true">
          <Icon />
        </span>
        <Sparkline values={sparkline} tone={tone} />
      </div>

      <div className="de-stat-copy">
        <p>{title}</p>
        <strong>{value}</strong>
        <span>{meta}</span>
      </div>
    </article>
  );
}

function Sparkline({ values, tone = "green" }) {
  const width = 120;
  const height = 40;
  const padding = 4;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const step = values.length > 1 ? (width - padding * 2) / (values.length - 1) : 0;

  const points = values.map((value, index) => {
    const x = padding + index * step;
    const normalized = (value - min) / span;
    const y = height - padding - normalized * (height - padding * 2);
    return `${x},${y}`;
  });

  const areaPath = [
    `M ${padding} ${height - padding}`,
    ...points.map((point) => `L ${point}`),
    `L ${width - padding} ${height - padding}`,
    "Z",
  ].join(" ");

  return (
    <svg className={`de-sparkline de-sparkline--${tone}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <defs>
        <linearGradient id={`spark-${tone}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#spark-${tone})`} />
      <polyline points={points.join(" ")} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StatusPill({ tone = "neutral", children }) {
  return <span className={`de-status-pill de-status-pill--${tone}`}>{children}</span>;
}

function Avatar({ name, image }) {
  const initials = buildInitials(name);

  return (
    <span className="de-avatar" aria-hidden="true">
      {image ? <img src={image} alt="" /> : initials}
    </span>
  );
}

function DateControl({ value, onChange }) {
  const inputRef = useRef(null);
  const formatted = formatLongDate(value);

  return (
    <div className="de-date-control">
      <button
        type="button"
        className="de-date-button"
        onClick={() => {
          if (typeof inputRef.current?.showPicker === "function") {
            inputRef.current.showPicker();
            return;
          }
          inputRef.current?.click();
        }}
      >
        <CalendarMonthOutlined />
        <span>{formatted}</span>
        <span className="de-date-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      <input
        ref={inputRef}
        className="de-date-input"
        type="date"
        value={formatDateInput(value)}
        onChange={(event) => {
          const next = event.target.value ? new Date(`${event.target.value}T00:00:00`) : new Date();
          onChange(next);
        }}
      />
    </div>
  );
}

function SectionHeader({ title, actionLabel, actionTo }) {
  return (
    <div className="de-section-head">
      <h2>{title}</h2>
      {actionTo ? (
        <Link to={actionTo} className="de-section-link">
          {actionLabel}
          <ArrowForwardRounded />
        </Link>
      ) : (
        <span className="de-section-link de-section-link--muted">{actionLabel}</span>
      )}
    </div>
  );
}

function RevenueChart({ values, labels }) {
  const width = 680;
  const height = 240;
  const paddingX = 20;
  const paddingY = 18;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const step = values.length > 1 ? (width - paddingX * 2) / (values.length - 1) : 0;

  const points = values.map((value, index) => {
    const x = paddingX + index * step;
    const normalized = (value - min) / span;
    const y = height - paddingY - normalized * (height - paddingY * 2);
    return { x, y };
  });

  const path = points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
  const areaPath = `${path} L ${points[points.length - 1]?.x ?? width - paddingX} ${height - paddingY} L ${paddingX} ${height - paddingY} Z`;

  return (
    <div className="de-revenue-chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="revenue-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0f8a4d" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#0f8a4d" stopOpacity="0" />
          </linearGradient>
        </defs>
        {Array.from({ length: 4 }, (_, index) => {
          const y = paddingY + ((height - paddingY * 2) / 3) * index;
          return <line key={index} x1={paddingX} x2={width - paddingX} y1={y} y2={y} />;
        })}
        <path d={areaPath} fill="url(#revenue-area)" />
        <path d={path} fill="none" stroke="#0f8a4d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((point, index) => (
          <circle key={`${point.x}-${point.y}`} cx={point.x} cy={point.y} r="4.5" />
        ))}
      </svg>

      <div className="de-revenue-labels" aria-hidden="true">
        {labels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function DonutChart({ centerValue, centerLabel, segments }) {
  const total = Math.max(
    segments.reduce((sum, segment) => sum + Math.max(segment.value, 0), 0),
    1
  );
  const normalized = segments.map((segment) => ({
    ...segment,
    percent: (Math.max(segment.value, 0) / total) * 100,
  }));

  let start = 0;
  const gradients = normalized.map((segment, index) => {
    const end = start + segment.percent;
    const gradient = `${segment.color} ${start}% ${end}%`;
    start = end;
    return gradient;
  });

  return (
    <div
      className="de-donut"
      style={{
        background: `conic-gradient(${gradients.join(", ")})`,
      }}
    >
      <div className="de-donut-inner">
        <strong>{centerValue}</strong>
        <span>{centerLabel}</span>
      </div>
    </div>
  );
}

function Panel({ title, actionLabel, actionTo, children, className = "" }) {
  return (
    <article className={`de-panel ${className}`.trim()}>
      <SectionHeader title={title} actionLabel={actionLabel} actionTo={actionTo} />
      {children}
    </article>
  );
}

function dashboardCompanyName(authUser, profileDetails, subCompanyName, summary) {
  return firstText(
    summary?.companyName,
    profileDetails?.user_name,
    profileDetails?.companyName,
    profileDetails?.sub_companyName,
    authUser?.sub_companyName,
    subCompanyName,
    authUser?.name,
    "Lamar Enterprise"
  );
}

export default function DashboardEntreprise() {
  const intl = useIntl();
  const formatMessage = intl.formatMessage;
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  const subCompanyName = useSelector((state) => state?.AuthReducer?.subCmpny || "");
  const profileDetailsRaw = useSelector((state) => state?.ProfileSettingsReducer?.profiledetails || {});
  const profileDetails = useMemo(() => readMaybeJson(profileDetailsRaw), [profileDetailsRaw]);

  const [dashboardData, setDashboardData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [activeRevenueView, setActiveRevenueView] = useState("month");
  const [loading, setLoading] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const appliedApiDateRef = useRef(false);

  useEffect(() => {
    let isActive = true;

    const loadOverview = async () => {
      setLoading(true);

      try {
        const response = await dashboardApi.getAdminDashboardDetails();
        if (!isActive) {
          return;
        }

        const payload = safeObject(response?.data?.data ?? response?.data);
        setDashboardData(Object.keys(payload).length > 0 ? payload : null);
        setLastSyncedAt(new Date());
      } catch (error) {
        if (!isActive) {
          return;
        }

        setDashboardData(null);
        setLastSyncedAt(null);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadOverview();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (appliedApiDateRef.current) {
      return;
    }

    const apiDate = firstText(dashboardData?.summary?.currentDate);
    if (!apiDate) {
      return;
    }

    const parsedDate = new Date(`${apiDate}T00:00:00`);
    if (isValidDate(parsedDate)) {
      setSelectedDate(parsedDate);
      appliedApiDateRef.current = true;
    }
  }, [dashboardData]);

  const dashboardModel = dashboardData ?? FALLBACK_DASHBOARD;
  const summary = safeObject(dashboardModel.summary);
  const kpi = safeObject(dashboardModel.kpi);
  const revenues = safeObject(dashboardModel.revenues);
  const recentReservations = safeObject(dashboardModel.recentReservations);
  const validationRequests = safeObject(dashboardModel.validationRequests);
  const alerts = safeObject(dashboardModel.alerts);
  const wallet = safeObject(dashboardModel.wallet);
  const fleetStats = useMemo(() => buildFleetStatsFromDashboard(dashboardModel), [dashboardModel]);

  const companyName = useMemo(
    () => dashboardCompanyName(authUser, profileDetails, subCompanyName, summary),
    [authUser, profileDetails, subCompanyName, summary]
  );

  const companyStatusMeta = useMemo(() => getCompanyStatusMeta(summary.companyStatus, formatMessage), [summary.companyStatus, formatMessage]);

  const currentTrips = useMemo(
    () =>
      safeArray(dashboardModel.ongoingTrips)
        .map((item, index) => normalizeOngoingTrip(item, index, formatMessage))
        .sort((a, b) => {
          const aTime = a.requestedDate ? new Date(a.requestedDate).getTime() : 0;
          const bTime = b.requestedDate ? new Date(b.requestedDate).getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 3),
    [dashboardModel.ongoingTrips, formatMessage]
  );

  const reservations = useMemo(
    () => safeArray(recentReservations.items).map((item, index) => normalizeReservationItem(item, index, formatMessage)).slice(0, 5),
    [recentReservations.items, formatMessage]
  );

  const topDrivers = useMemo(
    () =>
      safeArray(dashboardModel.topDrivers)
        .map((item, index) => normalizeTopDriverItem(item, index))
        .sort((a, b) => b.rating - a.rating || b.courses - a.courses)
        .slice(0, 3),
    [dashboardModel.topDrivers]
  );

  const activeDriversCount = toNumber(kpi.activeDrivers, summary.totalDrivers);
  const pendingDriversCount = Math.max(toNumber(summary.totalDrivers, 0) - activeDriversCount, 0);
  const selectedDayActiveTripsCount = toNumber(kpi.tripsToday, summary.tripsToday);
  const selectedDayCancelledTripsCount = toNumber(recentReservations.cancellationsToday, 0);
  const selectedDayRevenue = toNumber(revenues.totalToday, 0);
  const monthRevenue = toNumber(kpi.revenueMonth, revenues.totalMonth);
  const weekRevenue = toNumber(revenues.totalWeek, monthRevenue);
  const walletBalance = toNumber(wallet.availableBalance, monthRevenue || selectedDayRevenue);
  const walletCurrency = firstText(wallet.currency, CURRENCY);
  const revenueGrowthPercent = toNumber(kpi.revenueGrowthPercent, 0);
  const revenueView = useMemo(
    () => buildRevenueSeriesFromDashboard(revenues, activeRevenueView, selectedDate),
    [revenues, activeRevenueView, selectedDate]
  );
  const chartSummaryLabel =
    activeRevenueView === "today" ? formatMessage({ id: "dashboard.chart.today" }) : activeRevenueView === "week" ? formatMessage({ id: "dashboard.chart.week" }) : formatMessage({ id: "dashboard.chart.month" });
  const selectedChartValue =
    activeRevenueView === "today" ? selectedDayRevenue : activeRevenueView === "week" ? weekRevenue : monthRevenue;
  const chartTrend = formatMessage({ id: "dashboard.stat.vsPrevious" }, { percent: formatPercent(revenueGrowthPercent) });
  const usingFallbackData = !dashboardData;
  const currentTripsLabel = "Voir tous les trajets";

  const reservationMeta = useMemo(
    () => [
      {
        value: recentReservations.newReservations,
        label: formatMessage({ id: "dashboard.reservation.new" }),
        tone: "success",
      },
      {
        value: recentReservations.pendingRequests,
        label: formatMessage({ id: "dashboard.reservation.pending" }),
        tone: "warning",
      },
      {
        value: recentReservations.cancellationsToday,
        label: formatMessage({ id: "dashboard.reservation.cancelled" }),
        tone: "danger",
      },
    ],
    [
      recentReservations.cancellationsToday,
      recentReservations.newReservations,
      recentReservations.pendingRequests,
      formatMessage,
    ]
  );

  const alertItems = useMemo(() => buildAlertItemsFromDashboard(alerts, formatMessage), [alerts, formatMessage]);
  const validationRows = useMemo(() => buildValidationRowsFromDashboard(validationRequests, formatMessage), [validationRequests, formatMessage]);

  const selectedDateHint = loading
    ? formatMessage({ id: "dashboard.hint.loading" })
    : usingFallbackData
      ? formatMessage({ id: "dashboard.hint.demo" })
      : formatMessage({ id: "dashboard.hint.lastSync" }, { time: lastSyncedAt ? formatTime(lastSyncedAt) : "--:--" });

  return (
    <div className="de-dashboard-shell">
      <section className="de-hero">
        <div className="de-hero-copy">
          <span className="de-kicker">{formatMessage({ id: "dashboard.kicker" })}</span>
          <h1>{formatMessage({ id: "dashboard.greeting" }, { company: companyName })}</h1>
          <p>{formatMessage({ id: "dashboard.description" })}</p>
          <div className="de-hero-meta">
            <span className="de-hero-pill">
              <CircleRounded />
              {companyStatusMeta.label}
            </span>
            <span className="de-hero-pill">
              <PeopleAltOutlined />
              {formatMessage({ id: "dashboard.pill.drivers" }, { count: formatNumber(summary.totalDrivers || activeDriversCount) })}
            </span>
            <span className="de-hero-pill">
              <DirectionsCarOutlined />
              {formatMessage({ id: "dashboard.pill.vehicles" }, { count: formatNumber(summary.totalVehicles || fleetStats.total) })}
            </span>
            <span className="de-hero-pill">
              <RouteOutlined />
              {formatMessage({ id: "dashboard.pill.trips" }, { count: formatNumber(selectedDayActiveTripsCount) })}
            </span>
          </div>
          <p className="de-hero-hint">{selectedDateHint}</p>
        </div>

        <div className="de-hero-actions">
          <DateControl value={selectedDate} onChange={setSelectedDate} />
          <Link to="/dispatcher" className="de-primary-button">
            <AddRounded />
            {formatMessage({ id: "dashboard.cta.createTrip" })}
          </Link>
        </div>
      </section>

      <section className="de-stat-grid">
        <StatCard
          icon={PeopleAltOutlined}
          title={formatMessage({ id: "dashboard.stat.activeDrivers" })}
          value={formatNumber(activeDriversCount)}
          meta={
            kpi.driverEvolution
              ? formatMessage({ id: "dashboard.stat.vsPrevious" }, { percent: formatPercent(kpi.driverEvolution.percentChange) })
              : formatMessage({ id: "dashboard.stat.pending" }, { count: formatNumber(pendingDriversCount) })
          }
          tone="green"
          sparkline={FALLBACK_SERIES.week}
        />
        <StatCard
          icon={DirectionsCarOutlined}
          title={formatMessage({ id: "dashboard.stat.availableVehicles" })}
          value={`${formatNumber(fleetStats.available)} / ${formatNumber(fleetStats.total)}`}
          meta={formatMessage({ id: "dashboard.stat.inMaintenance" }, { count: formatNumber(fleetStats.maintenance) })}
          tone="blue"
          sparkline={FALLBACK_SERIES.month}
        />
        <StatCard
          icon={RouteOutlined}
          title={formatMessage({ id: "dashboard.stat.tripsToday" })}
          value={formatNumber(selectedDayActiveTripsCount)}
          meta={formatMessage({ id: "dashboard.stat.inProgress" }, { count: formatNumber(kpi.tripsInProgress || currentTrips.length) })}
          tone="amber"
          sparkline={FALLBACK_SERIES.today}
        />
        <StatCard
          icon={AccountBalanceWalletOutlined}
          title={formatMessage({ id: "dashboard.stat.monthRevenue" })}
          value={formatCurrency(monthRevenue, walletCurrency)}
          meta={chartTrend}
          tone="green"
          sparkline={FALLBACK_SERIES.month}
        />
      </section>

      <section className="de-triple-grid">
        <article className="de-panel">
          <SectionHeader title={formatMessage({ id: "dashboard.panel.alerts" })} actionLabel={formatMessage({ id: "dashboard.panel.seeAllAlerts" })} actionTo="/corporate-vehicles" />
          <div className="de-alert-list">
            {alertItems.map((item, index) => {
              const Icon = item.icon || TaskAltRounded;
              return (
                <div key={item.title || index} className={`de-alert-item de-alert-item--${item.tone || "neutral"}`}>
                  <div className="de-alert-icon">
                    <Icon /> 
                  </div>
                  <div className="de-alert-copy">
                    <strong>{item.title}</strong>
                    <span>{item.detail}</span>
                  </div>
                  <ArrowForwardRounded className="de-alert-arrow" />
                </div>
              );
            })}
          </div>
          <Link to="/corporate-vehicles" className="de-panel-action de-panel-action--compact">
            {formatMessage({ id: "dashboard.panel.seeAllAlerts" })}
          </Link>
        </article>

        <article className="de-panel">
          <SectionHeader title={formatMessage({ id: "dashboard.panel.validations" })} actionLabel={formatMessage({ id: "dashboard.panel.seeAllValidations" })} actionTo="/corporate-drivers" />
          <div className="de-validation-list">
            {validationRows.map((item) => (
              <div key={item.label} className={`de-validation-item de-validation-item--${item.tone}`}>
                <div>
                  <strong>{item.label}</strong>
                  <span>{formatMessage({ id: "dashboard.panel.processNow" })}</span>
                </div>
                <span className="de-validation-badge">{formatNumber(item.value)}</span>
              </div>
            ))}
          </div>
          <Link to="/corporate-drivers" className="de-panel-action de-panel-action--primary">
            {formatMessage({ id: "dashboard.panel.validateNow" })}
          </Link>
        </article>

        <article className="de-panel de-wallet-panel">
          <SectionHeader title={formatMessage({ id: "dashboard.panel.wallet" })} actionLabel={formatMessage({ id: "dashboard.panel.seeDetail" })} actionTo="/cashoutrecords" />
          <div className="de-wallet-hero">
            <div className="de-wallet-copy">
              <span>{formatMessage({ id: "dashboard.wallet.balance" })}</span>
              <strong>{formatCurrency(walletBalance, walletCurrency)}</strong>
              <small>{formatMessage({ id: "dashboard.wallet.synced" })}</small>
            </div>
            <div className="de-wallet-visual" aria-hidden="true">
              <div className="de-wallet-outline" />
              <div className="de-wallet-card">
                <AccountBalanceWalletOutlined />
              </div>
            </div>
          </div>

          <div className="de-wallet-actions">
            <button type="button" className="de-wallet-action">
              <span>+</span>
              {formatMessage({ id: "dashboard.wallet.recharge" })}
            </button>
            <button type="button" className="de-wallet-action">
              <span>−</span>
              {formatMessage({ id: "dashboard.wallet.withdraw" })}
            </button>
            <Link to="/cashoutrecords" className="de-wallet-action">
              <HistoryRounded />
              {formatMessage({ id: "dashboard.wallet.history" })}
            </Link>
            <Link to="/cashoutsheets" className="de-wallet-action">
              <PaymentsOutlined />
              {formatMessage({ id: "dashboard.wallet.driverPayments" })}
            </Link>
          </div>

          <Link to="/cashoutrecords" className="de-panel-action de-panel-action--compact">
            {formatMessage({ id: "dashboard.wallet.seeAllTransactions" })}
          </Link>
        </article>
      </section>
    </div>
  );
}

